import { format } from "date-fns";
import { CalendarIcon, Clock, Trash2 } from "lucide-react";
import { useState } from "react";

import { useAddCurfewMutation, useRemoveCurfewMutation } from "@/app/api/slices/curfewsApiSlice";
import { ButtonLoader } from "@/components/ButtonLoader";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useCurrentVesselId } from "@/hooks/useCurrentVesselId";
import { cn } from "@/lib/utils";
import { CurfewEntry } from "@/types/Curfew";
import { handleServerErrors } from "@/utils/handleServerErrors";

type Props = {
	curfews: CurfewEntry[];
	isLoading: boolean;
};

export const CurfewPlanner = ({ curfews, isLoading }: Props) => {
	const { toast } = useToast();
	const vesselId = useCurrentVesselId();

	const [date, setDate] = useState<Date>();
	const [time, setTime] = useState<string>("");

	const [addCurfewMutation, { isLoading: isAddingCurfew }] = useAddCurfewMutation();
	const [removeCurfewMutation, { isLoading: isRemovingCurfew }] = useRemoveCurfewMutation();

	const handleAddCurfew = async () => {
		if (!date || !time) return;

		const newCurfew: {
			date: string;
			time: string;
		} = {
			date: format(date, "yyyy-MM-dd"),
			time: time,
		};

		await addCurfewMutation({ vesselId, data: newCurfew })
			.unwrap()
			.then((payload) => {
				toast({
					variant: "success",
					title: payload?.message || "Successfully added curfew.",
				});

				// clear form
				setDate(undefined);
				setTime("");
			})
			.catch((error) => handleServerErrors(error, toast));
	};

	const handleDeleteCurfew = async (id: string) => {
		await removeCurfewMutation({ vesselId, curfewId: id })
			.unwrap()
			.then((payload) => {
				toast({
					variant: "success",
					title: payload?.message || "Successfully removed curfew.",
				});
			})
			.catch((error) => handleServerErrors(error, toast));
	};

	// Generate time options in 30-minute intervals
	const timeOptions = [];
	for (let hour = 0; hour < 24; hour++) {
		for (const minute of [0, 30]) {
			const formattedHour = hour.toString().padStart(2, "0");
			const formattedMinute = minute.toString().padStart(2, "0");
			timeOptions.push(`${formattedHour}:${formattedMinute}`);
		}
	}

	return (
		<Card>
			<CardContent className="space-y-6 p-6">
				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="date">Select Date</Label>
						<Popover>
							<PopoverTrigger asChild>
								<Button
									id="date"
									variant="outline"
									className={cn(
										"w-full justify-start text-left font-normal",
										!date && "text-muted-foreground",
									)}
								>
									<CalendarIcon className="mr-2 h-4 w-4" />
									{date ? format(date, "PPP") : <span>Pick a date</span>}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto p-0">
								<Calendar
									mode="single"
									selected={date}
									onSelect={setDate}
									disabled={{ before: new Date() }}
								/>
							</PopoverContent>
						</Popover>
					</div>

					<div className="space-y-2">
						<Label htmlFor="time">Curfew Time</Label>
						<Select value={time} onValueChange={setTime}>
							<SelectTrigger id="time" className="w-full">
								<SelectValue placeholder="Select time">
									{time ? (
										<div className="flex items-center">
											<Clock className="mr-2 h-4 w-4" />
											{time}
										</div>
									) : (
										"Select time"
									)}
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								{timeOptions.map((timeOption) => (
									<SelectItem key={timeOption} value={timeOption}>
										{timeOption}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<Button
					onClick={handleAddCurfew}
					disabled={!date || !time || isAddingCurfew}
					className="w-full"
				>
					<ButtonLoader isLoading={isAddingCurfew} />
					Add Curfew
				</Button>

				<div className="space-y-2">
					<h3 className="text-lg font-medium">Scheduled Curfews</h3>
					{isLoading ? (
						<ScrollArea className="h-[200px] rounded-md border">
							<div className="space-y-2 p-4">
								<div className="flex items-center justify-between rounded-lg border p-3">
									<Skeleton className="h-11 w-full" />
								</div>
								<div className="flex items-center justify-between rounded-lg border p-3">
									<Skeleton className="h-11 w-full" />
								</div>
								<div className="flex items-center justify-between rounded-lg border p-3">
									<Skeleton className="h-11 w-full" />
								</div>
								<div className="flex items-center justify-between rounded-lg border p-3">
									<Skeleton className="h-11 w-full" />
								</div>
							</div>
						</ScrollArea>
					) : (
						<>
							{curfews.length === 0 ? (
								<p className="text-muted-foreground py-4 text-center">No curfews scheduled</p>
							) : (
								<ScrollArea className="h-[200px] rounded-md border">
									<div className="space-y-2 p-4">
										{curfews.map((curfew) => (
											<div
												key={curfew.id}
												className="flex items-center justify-between rounded-lg border p-3"
											>
												<div className="flex items-center space-x-4">
													<CalendarIcon className="text-muted-foreground h-5 w-5" />
													<div>
														<p className="font-medium">{format(curfew.date, "PPP")}</p>
														<p className="text-muted-foreground text-sm">
															Curfew at {curfew.time.split(":")[0]}:{curfew.time.split(":")[1]}
														</p>
													</div>
												</div>
												<Button
													variant="ghost"
													size="icon"
													style={{ background: "#F44336" }}
													disabled={isRemovingCurfew}
													onClick={() => handleDeleteCurfew(curfew.id)}
												>
													<Trash2 color="#fff" />
												</Button>
											</div>
										))}
									</div>
								</ScrollArea>
							)}
						</>
					)}
				</div>
			</CardContent>
		</Card>
	);
};
